// @ts-strict-ignore
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from '@node-space/ui-components'
import { logSentryError } from '@node-space/utils'
import { Approval } from 'constants/General'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useCreateAccountUserMutation } from 'hooks/mutations/useCreateAccountUserMutation'
import { useUpdateAccountUserMutation } from 'hooks/mutations/useUpdateAccountUserMutation'
import {
  TeamMemberEditorFormFields,
  teamMemberEditorSchema,
} from 'pages/TeamMembers/schemas/teamMemberEditorSchema'
import { type AccountUser, type AccountUserGroup } from 'types/accounts'
import { type FormFieldsAccountUser } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { mapTranslationsSubText } from '../utils/utils'

type UseTeamMembersFormProps = {
  accountUser?: AccountUser
  existingUser: string
  userGroups?: AccountUserGroup[]
  action: 'EDIT' | 'CREATE'
  canManageApprover?: boolean
  handleCloseModal: () => void
  getValues: () => void
}

export const useTeamMembersForm = ({
  accountUser,
  existingUser,
  userGroups,
  action,
  canManageApprover,
  handleCloseModal,
  getValues,
}: UseTeamMembersFormProps) => {
  const { t } = useTranslation()
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)

  const { mutate: mutateCreateUser, isPending: isLoadingCreateUser } =
    useCreateAccountUserMutation()
  const { mutate: mutateUpdateUser, isPending: isLoadingUpdateUser } =
    useUpdateAccountUserMutation()

  const formSchema = teamMemberEditorSchema(t)

  const form = useForm<TeamMemberEditorFormFields>({
    resolver: yupFormResolver<TeamMemberEditorFormFields>(formSchema),
    defaultValues: {
      emailAddress: accountUser?.emailAddress || '',
      groups: accountUser?.groups || [],
      ...(action === 'CREATE' && canManageApprover && { approver: false }),
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const { handleSubmit, setValue, watch, trigger } = form

  const formData = watch()
  const selectedRadioOption = formData?.groups?.[0]?.id?.toString() || existingUser

  const onRadioChange = (id: number | string) => {
    const selectedUserGroup = new Array(1).fill(userGroups?.find(group => group?.id == id))
    setValue('groups', selectedUserGroup, { shouldValidate: true })
  }

  const radioOptions = userGroups
    ?.filter(userGroup => (userGroup?.name as string) !== Approval.ACCOUNT_TRANSACTION_APPROVER)
    ?.map(group => {
      return {
        label: group?.name,
        value: `${group?.id}`,
        secondaryLabel: mapTranslationsSubText(group?.name, t),
      }
    })

  const handleSuccess = (isCreate: boolean) => {
    handleCloseModal()
    setErrorMessage(null)
    getValues()

    toast({
      variant: 'success',
      header: isCreate ? t('teamMember.addSuccessHeading') : t('teamMember.editSuccessHeading'),
      body: isCreate ? t('teamMember.addSuccessSubheading') : t('teamMember.editSuccessSubheading'),
    })
  }

  const createNewUser = (formData: FormFieldsAccountUser) => {
    mutateCreateUser(formData, {
      onSuccess: () => {
        handleSuccess(true)
        track.Amp.track(AmplitudeEvent.EOF_ADD_TEAM_MEMBER_SUCCESS, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.VIEW,
        })
      },
      onError: (error: Error) => {
        track.Amp.track(AmplitudeEvent.EOF_ADD_TEAM_MEMBER_ERROR, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.ERROR,
        })
        logSentryError(`Error from TeamMemberEditor - createNewUser`, error)
        setErrorMessage(error?.message || t('defaultAddTeamMemberError'))
      },
    })
  }

  const updateExistingUser = (formData: FormFieldsAccountUser) => {
    mutateUpdateUser(
      { userID: accountUser?.id, formData },
      {
        onSuccess: () => {
          handleSuccess(false)
          track.Amp.track(AmplitudeEvent.EOF_EDIT_TEAM_MEMBER_SUCCESS, {
            category: AmplitudeEventCategory.MERCHANT_PORTAL,
            action: AmplitudeEventAction.VIEW,
          })
        },
        onError: (error: Error) => {
          logSentryError(`Error from TeamMemberDelete - updateExistingUser`, error)
          setErrorMessage(error?.message || t('defaultEditTeamMemberError'))
        },
      }
    )
  }

  const saveUser = (formData: FormFieldsAccountUser) => {
    if (accountUser) {
      updateExistingUser(formData)
    } else {
      createNewUser(formData)
    }
  }

  const onSubmit = (data: TeamMemberEditorFormFields) => {
    setErrorMessage(null)
    const payload: FormFieldsAccountUser = {
      emailAddress: data.emailAddress,
      groups: data.groups,
      ...(data.approver !== undefined && { approver: data.approver }),
    }
    saveUser(payload)
  }

  return {
    form,
    formData,
    formErrors: form.formState.errors,
    errorMessage,
    selectedRadioOption,
    radioOptions,
    isLoading: isLoadingCreateUser || isLoadingUpdateUser,
    setErrorMessage,
    handleSubmit,
    onRadioChange,
    onSubmit,
    setValue,
    trigger,
  }
}
